// src/encounter/invoice-edit-service-dialog.tsx
// Per-LINE discount on an invoice service — the wrap-up row's Edit (pencil) action.
//
// FLUTTER SOURCE: `edit_service_popup.dart` (opened from wrap_up_billing_screen.dart:941-965
// `showEditServicePopup`). Despite the generic name it edits exactly TWO things —
// DISCOUNT AMOUNT and DISCOUNT REASON — then fires `AddRemoveDiscountOnServiceEvent`
// (:201-206) → `PUT /Billing/Invoice/UpdateServiceDiscount?invoiceId=` (:1848-1858).
// It does NOT edit fee, quantity or dates; those are separate per-line arrays.
//
// Both of Flutter's Save guards are ported with their exact copy (:189-200):
//   1. "Discount amount cannot be more than actual amount of service" — vs the line's NET.
//   2. "Discount reason cannot be blank".
//
// The reason is a DROPDOWN of the fixed discount-reason list, as in pm-react's Edit appointment
// → Billing tab (`edit-service-discount-dialog.tsx`). The chosen label ships as `discountReason`.
import { useState } from 'react';
import { Banner, Modal, Button, TextField, Select } from '@scripsteam/scrips-signal-ds';
import { DISCOUNT_REASON_OPTIONS, isDiscountReason } from '@/billing/lib/discount-reasons';

export interface InvoiceEditServiceDialogProps {
  open: boolean;
  onClose: () => void;
  /** The line being edited — label + current discount (amount and saved reason) + the NET the
   *  discount is checked against. */
  line: {
    id: string;
    label: string;
    netAmount: number;
    discountAmount: number;
    discountReason?: string;
  } | null;
  currencyCode: string;
  onSave: (v: { lineId: string; discountAmount: number; discountReason: string }) => void;
  busy?: boolean;
}

export function InvoiceEditServiceDialog({
  open,
  onClose,
  line,
  currencyCode,
  onSave,
  busy,
}: InvoiceEditServiceDialogProps) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Re-seed when a different line opens. `key`-ing the dialog from the host would also work;
  // this keeps the host simpler and the reset explicit.
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (open && line && seededFor !== line.id) {
    setSeededFor(line.id);
    setAmount(line.discountAmount ? String(line.discountAmount) : '');
    // Reopen shows the saved reason, as pm-react does. A legacy free-text reason that is not on
    // the list starts blank, so the picker never holds a value it cannot display.
    setReason(isDiscountReason(line.discountReason) ? line.discountReason : '');
    setError(null);
  }

  function close() {
    setSeededFor(null);
    setError(null);
    onClose();
  }

  function handleSave() {
    if (!line) return;
    const numeric = Number(amount);
    if (!Number.isFinite(numeric) || numeric < 0) {
      setError('Discount amount cannot be more than actual amount of service');
      return;
    }
    // Flutter's guard #1 — compares against the line's NET amount (:190-195).
    if (numeric > line.netAmount) {
      setError('Discount amount cannot be more than actual amount of service');
      return;
    }
    // Flutter's guard #2 (:196-200).
    if (!reason.trim()) {
      setError('Discount reason cannot be blank');
      return;
    }
    onSave({ lineId: line.id, discountAmount: numeric, discountReason: reason.trim() });
    close();
  }

  const footer = (
    <>
      <Button variant="secondary" onClick={close} disabled={busy}>
        Cancel
      </Button>
      <Button onClick={handleSave} disabled={!line || !!busy}>
        {busy ? 'Saving…' : 'Save'}
      </Button>
    </>
  );

  return (
    <Modal open={open} onClose={close} title={line?.label ?? 'Edit service'} footer={footer} size="sm">
      <div className="flex flex-col gap-4">
        <TextField
          label={`Discount Amount (${currencyCode})`}
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
          helperText={line ? `Net ${currencyCode} ${line.netAmount.toFixed(2)}` : undefined}
        />
        <Select
          label="Discount reason"
          value={reason}
          onChange={(v) => setReason(v ?? '')}
          placeholder="Select a reason"
          options={DISCOUNT_REASON_OPTIONS}
        />
        {error && (
          <Banner variant="error" role="alert" title="Discount not applied">
            {error}
          </Banner>
        )}
      </div>
    </Modal>
  );
}
